import { Link } from "react-router-dom";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@akuna/ui";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@akuna/ui";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useNfseReviewData } from "@/hooks/useNfseReviewData";
import type { NfseReadinessCheck } from "@/lib/fiscal/nfse/readiness";

interface SaleNfseReviewDialogProps {
  saleId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isIssuing: boolean;
  onConfirm: () => void;
}

function targetHref(target: NfseReadinessCheck["target"], itemId?: string | null) {
  if (target === "clinicSettings") return "/taxes/settings";
  if (target === "serviceItem" && itemId) return `/catalog?itemId=${itemId}`;
  return "";
}

const moneyFormatter = new Intl.NumberFormat(undefined, {
  style: "currency",
  currency: "BRL",
});

export function SaleNfseReviewDialog({
  saleId,
  open,
  onOpenChange,
  isIssuing,
  onConfirm,
}: SaleNfseReviewDialogProps) {
  const { t } = useTranslation("erp");
  const { data, isLoading, error } = useNfseReviewData(saleId, open);

  const blocking = data?.readiness.blocking ?? [];
  const warnings = data?.readiness.warnings ?? [];
  const canIssue = Boolean(data?.readiness.canIssue) && !isIssuing;
  const emptyValue = t("saleDetail.emptyValue");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] min-h-0 max-w-3xl flex flex-col gap-0 p-0">
        <DialogHeader className="px-6 pb-3 pt-6">
          <DialogTitle>{t("nfse.review.title")}</DialogTitle>
          <DialogDescription>{t("nfse.review.description")}</DialogDescription>
        </DialogHeader>

        <ScrollArea className="min-h-0 flex-1 px-6 pb-4">
          {isLoading ? (
            <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("common.loading")}
            </div>
          ) : error ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              {error instanceof Error ? error.message : t("nfse.review.loadError")}
            </div>
          ) : data ? (
            <div className="space-y-4">
              <section className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <h4 className="font-medium">{t("nfse.review.profile")}</h4>
                  <Badge variant="outline">
                    {data.profile.municipalityName}/{data.profile.uf}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">{t("nfse.review.profileDescription")}</p>
              </section>

              <Separator />

              <section className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
                <div>
                  <h4 className="mb-1 font-medium">{t("nfse.review.provider")}</h4>
                  <p className="text-muted-foreground">{String(data.settings?.company_name ?? emptyValue)}</p>
                  <p className="text-muted-foreground">{String(data.settings?.cnpj ?? emptyValue)}</p>
                  <p className="text-muted-foreground">
                    {String(data.settings?.municipal_registration ?? emptyValue)}
                  </p>
                </div>
                <div>
                  <h4 className="mb-1 font-medium">{t("nfse.review.receiver")}</h4>
                  <p className="text-muted-foreground">{data.buyer.name || emptyValue}</p>
                  <p className="text-muted-foreground">{data.buyer.document || emptyValue}</p>
                </div>
              </section>

              <Separator />

              <section className="space-y-2">
                <h4 className="font-medium">{t("nfse.review.services")}</h4>
                {data.serviceItems.map((item) => (
                  <div key={item.id} className="rounded-md border border-border p-3 text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-medium">{item.itemName}</span>
                      <span>{moneyFormatter.format(item.lineTotal)}</span>
                    </div>
                    <p className="text-muted-foreground">
                      {t("nfse.review.serviceListItem")}: {item.fiscal?.nfse_service_list_item || emptyValue}
                    </p>
                    <p className="text-muted-foreground">
                      {t("nfse.review.issRate")}:{" "}
                      {item.fiscal?.nfse_iss_rate != null ? `${item.fiscal.nfse_iss_rate}%` : emptyValue}
                    </p>
                  </div>
                ))}
              </section>

              <Separator />

              <section className="space-y-2">
                <h4 className="font-medium">{t("nfse.review.pending")}</h4>
                {blocking.length === 0 && warnings.length === 0 ? (
                  <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900 dark:border-emerald-900/40 dark:bg-emerald-900/20 dark:text-emerald-100">
                    <CheckCircle2 className="h-4 w-4" />
                    {t("nfse.review.noPendingIssues")}
                  </div>
                ) : null}
                {[...blocking, ...warnings].map((check, index) => {
                  const href = targetHref(
                    check.target,
                    typeof check.context?.itemId === "string" ? check.context.itemId : null,
                  );
                  return (
                    <div
                      key={`${check.code}-${check.target}-${check.context?.lineId ?? index}`}
                      className="rounded-md border border-border p-3 text-sm"
                    >
                      <div className="flex items-start gap-2">
                        <AlertCircle className="mt-0.5 h-4 w-4 text-amber-600" />
                        <div className="space-y-1">
                          <p>{t(check.messageKey, check.context ?? {})}</p>
                          {href ? (
                            <Button variant="link" className="h-auto p-0" asChild>
                              <Link to={href} onClick={() => onOpenChange(false)}>
                                {t("nfse.review.fixIssue")}
                              </Link>
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </section>
            </div>
          ) : null}
        </ScrollArea>

        <DialogFooter className="border-t border-border px-6 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.close")}
          </Button>
          <Button onClick={onConfirm} disabled={!canIssue}>
            {isIssuing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {t("nfse.review.confirmIssue")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
